import {useEffect, useMemo} from 'react'

import cn from 'classnames/bind'

import PricingSection from 'components/blocks/PricingSection'
import {Col, Container, Row} from 'components/elements/grid'
import HeadingSection from 'components/elements/HeadingSection'
import paymentPlansConfig from 'src/configs/paymentPlans.config'
import {TPageComponent, TPriceColor} from 'src/types'
import {klaviyoTrack, posthogTrack} from 'src/utils'
import {useAuthStore} from 'src/utils/contexts/RootStore.context'

import s from './TemplatePricing.module.scss'

const cx = cn.bind(s)

interface ITemplatePricingProps {
	pricingSlug?: string
	className?: string
}

export interface IPriceCardItem {
	id: number
	color: TPriceColor
	slug: string
	title: string
	currency: string
	price: number
	priceTitle: string
	extraPrice?: number
	label?: string
	quantity?: number
	benefits?: string[]
	href: string
}

type TPaymentPlansKeysExtended = TPaymentPlansKeys | 'free'

type TPricingCardsItems = {
	[key in TPaymentPlansKeysExtended]?: IPriceCardItem
}

export const pricingCardsItems: TPricingCardsItems = {
	monthly: {
		id: 1,
		color: 'light-blue',
		title: 'trial',
		slug: 'monthly',
		benefits: [
			'Premium Monthly membership',
			'Print and export unlimited Pay Stubs. Full access to our library of tools.',
			'Automatically renews each month. Cancel anytime.',
		],
		currency: '$',
		price: paymentPlansConfig['monthly'].subscription.price,
		priceTitle: '/month',
		href: '/checkout/payment/monthly',
	},
	annual: {
		id: 2,
		color: 'light-peach',
		title: 'Annual',
		slug: 'annual',
		currency: '$',
		benefits: [
			'Premium Annual membership',
			'Print and export unlimited Pay Stubs. Full access to our library of tools.',
			'Charged as one upfront payment of $89 and automatically renews annually thereafter.',
		],
		price: paymentPlansConfig['annual'].subscription.price,
		priceTitle: '/year',
		href: '/checkout/payment/annual',
	},
	single: {
		id: 3,
		color: 'light-purple',
		title: 'Just This Document',
		benefits: [
			'Purchase your completed document',
			'Print and export your document in PDF format',
			'30 day unconditional money back guarantee',
		],
		slug: 'single',
		currency: '$',
		price: paymentPlansConfig['single'].subscription.price,
		priceTitle: '',
		href: '/checkout/payment/single',
	},
	free: {
		id: 4,
		color: 'light-blue',
		title: 'Free',
		slug: 'free',
		currency: '$',
		price: 0,
		priceTitle: '',
		href: '/checkout/payment/free',
	},
}

const getMonthlyCardItem = (monthlyPlanConfig: IPaymentPlanConfig): IPriceCardItem => {
	const {key, trial, subscription} = monthlyPlanConfig
	if (!!trial)
		return {
			id: 1,
			color: 'light-blue',
			title: 'trial',
			slug: key,
			benefits: [
				'Print and export unlimited Pay Stubs. Full access to our library of tools.',
				`Try us for ${trial?.duration || 7} days for only $${trial?.price}`,
				`Renews at $${subscription.price} on the ${
					(trial?.duration || 7) + 1
				}th day, and automatically renews each month. Cancel anytime.`,
			],
			currency: '$',
			price: trial?.price || 1.99,
			priceTitle: `/for ${trial?.duration || 7} days`,
			href: `/checkout/payment/${key}`,
		}

	return {
		id: 1,
		color: 'light-blue',
		title: 'Monthly',
		slug: key,
		currency: '$',
		benefits: [
			'Premium Monthly membership',
			'Print and export unlimited Pay Stubs. Full access to our library of tools.',
			'Automatically renews each month. Cancel anytime.',
		],
		price: subscription.price,
		priceTitle: '/month',
		href: `/checkout/payment/${key}`,
	}
}

const TemplatePricing: TPageComponent<ITemplatePricingProps> = ({className, pricingSlug}) => {
	const authStore = useAuthStore()

	useEffect(() => {
		posthogTrack('[PRICING] Page opened')
		klaviyoTrack('Page opened', {page: 'pricing', href: window?.location?.href})
	}, [])

	useEffect(() => {
		if (!authStore.hasActiveSubscription) {
			posthogTrack(`[PRICING unsubscribed] page opened`)
		}
	}, [authStore.hasActiveSubscription])

	const preparedPricingCardsItems = useMemo(() => {
		const config = paymentPlansConfig['monthly-b']

		pricingCardsItems.monthly = getMonthlyCardItem(config)
		return pricingCardsItems
	}, [])

	return (
		<div className={cx('page-wrapper', className)}>
			<Container>
				<Row>
					<Col>
						<HeadingSection className={s.heading}>Our great offers</HeadingSection>
					</Col>
				</Row>
			</Container>
			<PricingSection
				pricingItems={Object.values(preparedPricingCardsItems).filter(
					(priceCard): priceCard is IPriceCardItem => priceCard.slug !== 'free',
				)}
			/>
		</div>
	)
}

TemplatePricing.displayName = 'TemplatePricing'

export default TemplatePricing
